/**
 * Qinggong placements in the Yanyu region (docs/archive/OPEN_WORLD.md §3, docs/archive/QINGGONG.md §6–7): the
 * boxes that make the routes (the inner bay's extra stepping stones, moored boats and their masts,
 * the leaping gorge's ledge stage) and the updrafts (ลมยก) that `systems/wind.ts` reads.
 *
 * Data only. The boxes are greybox masses like `yanyu.blockout.ts`'s (whose list takes them last,
 * so they stand over the terrain and the boxes before them); a detail lane replaces them by id.
 * Metres in the region: x east, y south; heights in metres above the sea.
 */
import type { Updraft } from '../qinggong';
import { CHUNK } from '../../region/chunk';
import { baseCell, compileSketch, RK, SKETCH_CELL, type BlockoutBox, type XY } from '../../region/sketch';
import { YANYU_REGION } from './yanyu';

const boxes: BlockoutBox[] = [];

// ── The bay's stepping stones (the user's decision, 2026-10-07) ─────────────────────────────────
// The sketch lays a stone about every 50 m along each leg (`stoneLegs`): enough for water skim at
// level 4 on the outer legs. On the inner legs (the sheltered water between the village, the
// mid-bay rock and the three-pine island) a stone goes between each pair, so the water between
// stones is under 30 m and level 1 crosses (`qinggong.test.ts`). Every stone stands 0.7 m over the
// sea, under a step up (`STEP_UP`, 0.8 m), so a skimmer walks onto it. Where a leg comes ashore on
// an island's rocky edge, steps cut into it (`LANDING`) climb from the water to the top.
/** Indices into `YANYU_REGION.stoneLegs` of the inner bay's legs. */
export const INNER_LEGS = [0, 2] as const;
/** The longest stretch of water (metres, stone centre to centre) an inner leg leaves. */
export const INNER_SPACING = 30;
/**
 * Landing steps where an inner leg meets an island: each `rise` metres over the last (a plain
 * jump, 2.9 m, clears it), `run` metres apart along the leg, `size` metres square, until the
 * island's ground is a jump away.
 */
export const LANDING = { rise: 2.5, run: 3, size: 4, max: 10 } as const;

const C = compileSketch(YANYU_REGION);
/** The sketch at the 4 m cell holding (x, y), before the boxes: its height and whether it is sea. */
function cellAt(x: number, y: number): { h: number; sea: boolean } {
  const cx = (Math.floor(x / SKETCH_CELL) + 0.5) * SKETCH_CELL;
  const cy = (Math.floor(y / SKETCH_CELL) + 0.5) * SKETCH_CELL;
  const c = baseCell(C, C.bucket(Math.floor(cx / CHUNK), Math.floor(cy / CHUNK)), cx, cy);
  return { h: c.h, sea: c.kind === RK.SEA };
}

/** A point `t` metres along a leg. */
const along = (leg: readonly [XY, XY], t: number): XY => {
  const [[x0, y0], [x1, y1]] = leg;
  const L = Math.hypot(x1 - x0, y1 - y0);
  return [x0 + ((x1 - x0) * t) / L, y0 + ((y1 - y0) * t) / L];
};
const legLength = ([[x0, y0], [x1, y1]]: readonly [XY, XY]): number => Math.hypot(x1 - x0, y1 - y0);
/** How far along a leg a point lies. */
const tOf = (leg: readonly [XY, XY], p: XY): number => {
  const [[x0, y0], [x1, y1]] = leg;
  return ((p[0] - x0) * (x1 - x0) + (p[1] - y0) * (y1 - y0)) / legLength(leg);
};
const onLeg = (leg: readonly [XY, XY], p: XY): boolean => {
  const [[x0, y0], [x1, y1]] = leg;
  return Math.abs((p[0] - x0) * (y1 - y0) - (p[1] - y0) * (x1 - x0)) / legLength(leg) < 1;
};

/** The stretch of a leg over water: from where it leaves the shore to where it reaches the next. */
function waterSpan(leg: readonly [XY, XY]): readonly [number, number] {
  const L = legLength(leg);
  let a = 0;
  while (a < L && !cellAt(...along(leg, a)).sea) a += 1;
  let b = L;
  while (b > a && !cellAt(...along(leg, b)).sea) b -= 1;
  return [a, b];
}

/** The inner bay's extra stones: each gap over `INNER_SPACING` on the water split evenly. */
export function innerStones(): XY[] {
  const out: XY[] = [];
  for (const k of INNER_LEGS) {
    const leg = YANYU_REGION.stoneLegs[k];
    if (!leg) throw new Error(`yanyu.qinggong: no stone leg ${k}`);
    const [a, b] = waterSpan(leg);
    const ts = C.stones
      .filter((p) => onLeg(leg, p) && cellAt(...p).sea)
      .map((p) => tOf(leg, p))
      .filter((t) => t > a && t < b)
      .sort((x, y) => x - y);
    // From a stone's width off each shore.
    const pts = [a + 2, ...ts, b - 2];
    for (let i = 0; i + 1 < pts.length; i++) {
      const n = Math.ceil((pts[i + 1]! - pts[i]!) / INNER_SPACING);
      for (let s = 1; s < n; s++) out.push(along(leg, pts[i]! + ((pts[i + 1]! - pts[i]!) * s) / n));
    }
  }
  return out;
}

/** The landing steps at each island end of the inner legs. */
export function landingSteps(): { at: XY; top: number }[] {
  const out: { at: XY; top: number }[] = [];
  const L = LANDING;
  for (const k of INNER_LEGS) {
    const leg = YANYU_REGION.stoneLegs[k]!;
    const [a, b] = waterSpan(leg);
    for (const [shore, dir, end] of [
      [a, -1, 0],
      [b, 1, 1],
    ] as const) {
      const [ex, ey] = leg[end];
      if (!YANYU_REGION.islands.some((i) => Math.hypot(i.x - ex, i.y - ey) < 1)) continue;
      // Up from the water a rise at a time, while the island beyond is over a jump's reach.
      let top = 0;
      for (let n = 1; n <= L.max; n++) {
        const t = shore + dir * L.run * (n - 0.5);
        const beyond = Math.max(...[1, 2, 3].map((m) => cellAt(...along(leg, t + dir * (L.run / 2 + m))).h));
        if (beyond <= top + L.rise) break;
        top += L.rise;
        out.push({ at: along(leg, t), top });
      }
    }
  }
  return out;
}

innerStones().forEach(([x, y], k) =>
  boxes.push({
    id: `bay.stone.inner.${String(k + 1).padStart(2, '0')}`,
    place: 0,
    name: 'หินเหยียบข้ามอ่าว',
    lane: 'islands',
    kind: 'stone',
    x,
    y,
    w: 4,
    d: 4,
    h: 0.7,
    base: 0,
    note: 'an inner-bay stepping stone: water skim level 1 crosses',
  }),
);
landingSteps().forEach(({ at: [x, y], top }, k) =>
  boxes.push({
    id: `bay.landing.${String(k + 1).padStart(2, '0')}`,
    place: 0,
    name: 'ขั้นหินขึ้นเกาะ',
    lane: 'islands',
    kind: 'stone',
    x,
    y,
    w: LANDING.size,
    d: LANDING.size,
    h: top,
    base: 0,
    note: 'a step cut in an island’s rocky edge: from the water to the top, a jump at a time',
  }),
);

// ── Moored boats: the hull's deck and the mast's top are perches ───────────────────────────────
const boats: readonly { id: string; name: string; x: number; y: number }[] = [
  { id: 'bay.boat.village', name: 'เรือจอดหน้าท่าหยานหยู', x: 1520, y: 372 },
  { id: 'pirates.boat', name: 'อ่าวโจรสลัด เรือโจร', x: 2018, y: 292 },
];
for (const b of boats) {
  boxes.push({ id: b.id, place: 0, name: b.name, lane: 'islands', kind: 'pier', x: b.x, y: b.y, w: 3, d: 8, h: 0.75, base: 0, note: 'a moored boat: its deck is a perch a skimmer steps up onto' });
  boxes.push({
    id: `${b.id}.mast`,
    place: 0,
    name: `${b.name} เสากระโดง`,
    lane: 'islands',
    kind: 'tower',
    x: b.x,
    y: b.y,
    w: 1,
    d: 1,
    h: 7,
    on: b.id,
    note: 'the mast top: a one-metre perch 8 m over the sea',
  });
}

// ── 17 · เหวกระโจน: a ledge stage up the gorge's west wall (wall-run chains) ─────────────────────
// Eight ledges stacked against the wall, each 7 m over the one below (wall-run level 3 reaches
// 8.8 m) and 3 m deep, from the gorge's floor (60 m down) to 4 m under the rim.
export const GORGE_STAGE = { wallX: 2645, y: 1600, width: 6, rise: 7, depth: 3, n: 8 } as const;
{
  const G = GORGE_STAGE;
  for (let k = 1; k <= G.n; k++) {
    const w = G.depth * (G.n + 1 - k);
    boxes.push({
      id: `gorge.ledge.${k}`,
      place: 17,
      name: `เหวกระโจน ชะง่อนผา ${k}`,
      lane: 'valley',
      kind: 'ledge',
      x: G.wallX + w / 2,
      y: G.y,
      w,
      d: G.width,
      h: G.rise * k,
      on: 'gorge.pit',
      note: `a ledge ${G.rise * k} m over the gorge's floor`,
    });
  }
}

/** The qinggong boxes, stamped after the region's own (`yanyu.blockout.ts` appends them). */
export const YANYU_QINGGONG_BOXES: readonly BlockoutBox[] = boxes;

// ── Updrafts (ลมยก) ─────────────────────────────────────────────────────────────────────────
/**
 * The region's updrafts: beside the waterfalls, at the foot of the headland and the red cliff, in
 * the leaping gorge, and over the golden summit (the great glides' launch). `top` is metres above
 * the sea, `lift` metres a second.
 */
export const YANYU_UPDRAFTS: readonly Updraft[] = [
  { id: 'summit', x: 520, y: 1000, r: 45, top: 430, lift: 6 },
  { id: 'falls.cloud', x: 700, y: 790, r: 26, top: 165, lift: 7 },
  { id: 'falls.saran', x: 2335, y: 2175, r: 14, top: 60, lift: 5 },
  { id: 'falls.east', x: 2620, y: 1440, r: 14, top: 95, lift: 6 },
  { id: 'headland', x: 1080, y: 100, r: 26, top: 85, lift: 6 },
  { id: 'redcliff', x: 2700, y: 2470, r: 30, top: 230, lift: 7 },
  { id: 'gorge', x: 2660, y: 1700, r: 12, top: 45, lift: 7 },
  // The hub city (docs/archive/HUB.md §4): round the stone, at the pagoda's foot, and over the water beside
  // the rainbow bridge.
  { id: 'hub.stone', x: 1450, y: 1650, r: 16, top: 60, lift: 6 },
  { id: 'hub.pagoda', x: 880, y: 1590, r: 8, top: 82, lift: 6 },
  { id: 'hub.rainbow', x: 1757, y: 1675, r: 10, top: 45, lift: 5 },
];

/**
 * Round every spire of the stone forest and the sea stacks: an updraft `pad` metres wider than
 * the spire, rising to `over` metres above its top (so a glider caught in it can land on top).
 */
export const SPIRE_UPDRAFT = { pad: 8, over: 10, lift: 5 } as const;

/**
 * The village's own (village-local metres, docs/archive/YANYU.md): the spray of the falls pool under the
 * 15 m cliff. On the region map the core's origin is added (`systems/wind.ts`).
 */
export const YANYU_VILLAGE_UPDRAFTS: readonly Updraft[] = [
  { id: 'village.falls', x: 23.5, y: 68, r: 5, top: 25, lift: 5 },
];
